/**
 * Pure mapping from the server hospital row to the hospital-profile screen's
 * sections (M3-T08). No React — kept testable and the screen declarative.
 */
import type { BedState, ServiceFlags } from '../api/hospitals'
import type { BedTone } from '../components/BedBadge'
import type { IconName } from '../components/Icon'
import { bedTone } from './hospitalCard'

export type BedStat = {
  label: string
  free: number
  total: number
  tone: BedTone
}

/** The five bed types shown on the profile, in display order. Each pairs a
 * colour tone (via the shared `bedTone`) with its free/total text. */
export function bedStats(beds: BedState): BedStat[] {
  return [
    { label: 'ICU', free: beds.icu_free, total: beds.icu_total },
    { label: 'General', free: beds.general_free, total: beds.general_total },
    { label: 'Private', free: beds.private_free, total: beds.private_total },
    {
      label: 'Executive suites',
      free: beds.executive_free,
      total: beds.executive_total,
    },
    {
      label: 'Maternity',
      free: beds.maternity_free,
      total: beds.maternity_total,
    },
  ].map((b) => ({ ...b, tone: bedTone(b.free, b.total) }))
}

export type ProfileService = { name: string; icon: IconName; flags: string[] }

// One row per capability. `h24`/`night` name the per-service hours columns (if
// any); `schedulable` services carry the hospital-level Fast-track flag.
type ServiceSpec = {
  has: keyof ServiceFlags
  name: string
  icon: IconName
  h24?: keyof ServiceFlags
  night?: keyof ServiceFlags
  schedulable?: boolean
}

const SERVICE_SPECS: readonly ServiceSpec[] = [
  {
    has: 'has_emergency',
    name: 'Emergency',
    icon: 'sos',
    h24: 'emergency_24h',
    night: 'emergency_night',
  },
  {
    has: 'has_pharmacy',
    name: 'Drugs · pharmacy',
    icon: 'pill',
    h24: 'pharmacy_24h',
    night: 'pharmacy_night',
  },
  {
    has: 'has_lab',
    name: 'Labs',
    icon: 'flask',
    h24: 'lab_24h',
    night: 'lab_night',
    schedulable: true,
  },
  {
    has: 'has_imaging',
    name: 'Imaging',
    icon: 'image',
    h24: 'imaging_24h',
    night: 'imaging_night',
    schedulable: true,
  },
  { has: 'has_surgery', name: 'Surgery', icon: 'knife', schedulable: true },
  // No Ambulance row (M34-T05, ADR-0063, owner ruling Q8) — see hospitalCard.ts.
  {
    has: 'has_chronic_care',
    name: 'Chronic care',
    icon: 'heart',
    schedulable: true,
  },
  {
    has: 'has_specialists_onsite',
    name: 'Specialists on-site',
    icon: 'user',
    schedulable: true,
  },
]

/** The hospital's services, in display order, each with its hours/appointment
 * flags. Only capabilities the hospital actually has appear. */
export function profileServices(flags: ServiceFlags): ProfileService[] {
  return SERVICE_SPECS.filter((spec) => flags[spec.has]).map((spec) => {
    const tags: string[] = []
    if (spec.h24 && flags[spec.h24]) tags.push('24hr')
    if (spec.night && flags[spec.night]) tags.push('Night')
    if (spec.schedulable && flags.has_fast_track) tags.push('Fast-track')
    return { name: spec.name, icon: spec.icon, flags: tags }
  })
}

/** Google Maps directions deep link for the hospital's coordinates. */
export function directionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}
